import { Button, Flex } from 'antd';
import React from 'react';

import styles from './notification-message.styles';

/**
 * Props para el componente NotificationMessage.
 */
export type NotificationMessageProps = {
  /**
   * La clase de ícono de Bootstrap que se mostrará (p. Ej., 'bi-check-circle').
   */
  icon?: string;
  /**
   * El título del mensaje de notificación.
   */
  title?: string;
  /**
   * El contenido de texto principal del mensaje de notificación.
   */
  text?: string;
  /**
   * Una función de devolución de llamada que se ejecutará cuando se haga clic en el botón de acción principal.
   * Si se proporciona, se mostrará el botón principal (p. Ej., 'Reintentar').
   */
  primaryCallback?: () => void;
  /**
   * Una función de devolución de llamada que se ejecutará cuando se haga clic en el botón de acción secundario.
   * Si se proporciona, se mostrará el botón secundario (p. Ej., 'Volver').
   */
  secondaryCallback?: () => void;
};

/**
 * Un componente para mostrar un mensaje de notificación al usuario.
 * Puede incluir un ícono, un título, un cuerpo de texto y botones de acción primarios/secundarios.
 *
 * @param {NotificationMessageProps} props - Las props para el componente NotificationMessage.
 *
 * @returns {JSX.Element} El componente NotificationMessage renderizado.
 */
const NotificationMessage = ({
  icon = '',
  title = '',
  text = '',
  primaryCallback,
  secondaryCallback
}: Readonly<NotificationMessageProps>) => {
  return (
    <div style={styles.notificationMessage}>
      <Flex vertical style={styles.notificationMessage__container}>
        <div style={styles.notificationMessage__header}>
          {icon && (
            <div style={styles.notificationMessage__icon}>
              <i className={`bi ${icon}`}></i>
            </div>
          )}
          {title && <h2 style={styles.notificationMessage__title}>{title}</h2>}
        </div>
        {text && <p style={styles.notificationMessage__text}>{text}</p>}
        {(primaryCallback || secondaryCallback) && (
          <Flex gap='middle' style={styles.notificationMessage__actions}>
            {secondaryCallback && (
              <Button
                onClick={secondaryCallback}
                style={styles.notificationMessage__secondaryButton}>
                Volver
              </Button>
            )}
            {primaryCallback && (
              <Button
                type='primary'
                onClick={primaryCallback}
                style={styles.notificationMessage__primaryButton}>
                Reintentar
              </Button>
            )}
          </Flex>
        )}
      </Flex>
    </div>
  );
};

export default NotificationMessage;
